import styles from '../RichTextEditor.module.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { $patchStyleText } from '@lexical/selection';
import { $getSelection, $isRangeSelection, LexicalEditor } from 'lexical';
import { ToolbarButton, ToolbarButtonProps } from './ToolbarButton';
import { Sketch } from '@uiw/react-color';
import { useBool } from '@/utils';

export function applyColor(color: string, type: 'self' | 'background') {
  const selection = $getSelection();
  if ($isRangeSelection(selection)) {
    if (type === 'self') {
      $patchStyleText(selection, { color });
    } else {
      $patchStyleText(selection, { 'background-color': color });
    }
  }
}

type FontColorType = 'self' | 'background';

export function useFontColor(editor: LexicalEditor, type: FontColorType) {
  const [selectedColor, setSelectedColor] = useState<string>('#000000');

  const selectColor = useCallback(
    (color: string) => {
      setSelectedColor(color);

      editor.update(() => {
        applyColor(color, type);
      });
    },
    [editor, type],
  );

  return {
    onFontColorChange: selectColor,
    selectedColor,
  };
}

export interface ToolbarFontColorButtonProps extends ToolbarButtonProps {
  onFontColorChange: (color: string) => void;
  selectedColor: string;
}

export function ToolbarFontColorButton(props: ToolbarFontColorButtonProps) {
  const { selectedColor, onFontColorChange, ...btnProps } = props;
  const [open, setOpen, toggleOpen] = useBool(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, setOpen]);

  return (
    <>
      <ToolbarButton {...btnProps} onClick={toggleOpen} />

      {open && (
        <div className={styles.colorPickerPopover} ref={pickerRef}>
          <Sketch
            color={selectedColor}
            onChange={(shade) => onFontColorChange(shade.hex)}
          />
        </div>
      )}
    </>
  );
}
